import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchTransfers } from "@/lib/api";
import type { Transfer, TransferDetailState, TransferBadgeVariant } from "@/types";

interface UseTransfersResult {
  transfers: Transfer[];
  stats: { value: string; label: string; hint?: string | null; positiveHint?: boolean }[];
  loading: boolean;
  error: string | null;
  source: "api" | "mock";
  refresh: () => void;
}

function statusVariantFor(status: string): TransferBadgeVariant {
  const s = status.toLowerCase();
  if (s.includes("reconcil")) return "success";
  if (s.includes("fail") || s.includes("reject")) return "danger";
  if (s.includes("signing") || s.includes("broadcast")) return "info";
  if (s.includes("pending")) return "warning";
  return "neutral";
}

function stateFor(status: string): TransferDetailState {
  const s = status.toLowerCase();
  if (s.includes("reconcil")) return "reconciled";
  if (s.includes("reject")) return "rejected";
  if (s.includes("fail")) return "failed";
  if (s.includes("approval")) return "pending-approval";
  if (s.includes("screening") || s.includes("policy")) return "pending-policy";
  if (s.includes("signing") || s.includes("approved")) return "approved";
  if (s.includes("broadcast")) return "broadcast-confirmed";
  if (s.includes("draft")) return "draft";
  return "pending-policy";
}

function mapApiTransfer(raw: Record<string, unknown>): Transfer {
  const id = String(raw.id ?? raw.transferId ?? "");
  const direction = String(raw.direction ?? "send") as Transfer["direction"];
  const source = String(raw.source ?? raw.sourceWallet ?? "");
  const dest = String(raw.destination ?? raw.destinationAddress ?? "");
  const route = `${source} → ${dest}`;
  const asset = String(raw.asset ?? raw.assetSymbol ?? "");
  const amount = String(raw.amount ?? "0");
  const statusRaw = String(raw.status ?? "pending");
  const statusLabel = String(raw.statusLabel ?? statusRaw.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()));
  const statusVariant = statusVariantFor(statusRaw);
  const created = String(raw.createdAt ?? raw.created ?? "");
  const creator = String(raw.createdBy ?? raw.creator ?? "");
  const wallet = String(raw.walletId ?? raw.wallet ?? "");
  const state = stateFor(statusRaw);

  return {
    id, direction, route, asset, amount,
    status: statusRaw as Transfer["status"],
    statusLabel, statusVariant,
    created, creator, wallet, state,
  };
}

function computeStats(transfers: Transfer[]) {
  const total = transfers.length;
  const pending = transfers.filter(t => t.state === "pending-approval" || t.state === "pending-policy").length;
  const failed = transfers.filter(t => t.state === "failed").length;
  const volumeUsd = transfers.reduce((sum, t) => {
    const n = parseFloat(t.amount.replace(/,/g, ""));
    return sum + (isNaN(n) ? 0 : n);
  }, 0);
  const volumeLabel = volumeUsd >= 1_000_000 ? `$${(volumeUsd / 1_000_000).toFixed(1)}M` : `$${Math.round(volumeUsd).toLocaleString()}`;

  return [
    { value: String(total), label: "Total (30d)" },
    { value: String(pending), label: "Pending Approval", hint: pending > 0 ? null : null, positiveHint: false },
    { value: String(failed), label: "Stuck / Failed", hint: null, positiveHint: false },
    { value: volumeLabel, label: "Volume Today", hint: null, positiveHint: false },
  ];
}

export function useTransfers(): UseTransfersResult {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "mock">("api");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTransfers();
      if (result.reasonCode) {
        setTransfers([]);
        setSource("api");
        setError(result.reasonCode);
      } else if (result.transfers.length === 0) {
        setTransfers([]);
        setSource("api");
      } else {
        setTransfers(result.transfers.map(mapApiTransfer));
        setSource("api");
      }
    } catch (error) {
      setTransfers([]);
      setSource("api");
      setError(error instanceof Error ? error.message : "Network error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => computeStats(transfers), [transfers]);

  return { transfers, stats, loading, error, source, refresh: load };
}
